import React, { useState } from 'react';
import {
  BellOutlined,
  CheckCircleOutlined,
  CloudUploadOutlined,
  DownOutlined,
  ExclamationCircleOutlined,
  HeartOutlined,
  HomeOutlined,
  InfoCircleOutlined,
  LikeOutlined,
  LoadingOutlined,
  PlusOutlined,
  SearchOutlined,
  SettingOutlined,
  SmileOutlined,
  StarOutlined,
  SyncOutlined,
  UserOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Anchor,
  Avatar,
  Badge,
  Breadcrumb,
  Button,
  Calendar,
  Card,
  Carousel,
  Checkbox,
  Col,
  Collapse,
  ColorPicker,
  ConfigProvider,
  DatePicker,
  Descriptions,
  Divider,
  Drawer,
  Dropdown,
  Empty,
  Flex,
  Form,
  Input,
  InputNumber,
  Layout,
  List,
  Menu,
  message,
  Modal,
  notification,
  Pagination,
  Popconfirm,
  Popover,
  Progress,
  Radio,
  Rate,
  Result,
  Row,
  Select,
  Skeleton,
  Slider,
  Space,
  Spin,
  Statistic,
  Steps,
  Switch,
  Table,
  Tabs,
  Tag,
  theme as antTheme,
  Timeline,
  TimePicker,
  Tooltip,
  Transfer,
  Tree,
  Typography,
  Upload,
} from 'antd';

import EventSeverityIndicator from '@totem/components/services/threats/EventSeverityIndicator';
import IBStatistic, {
  statisticsStyles,
} from '@totem/components/ui_components/widgets/statistic/IBStatistic';
import { darkThemeData, lightThemeData } from '@totem/styles/theme';

const { Title, Text, Paragraph, Link } = Typography;

// ─── Static Data ─────────────────────────────────────────────────────────────

const tableColumns = [
  { title: 'Name', dataIndex: 'name', key: 'name' },
  { title: 'Age', dataIndex: 'age', key: 'age' },
  { title: 'Address', dataIndex: 'address', key: 'address' },
  {
    title: 'Action',
    key: 'action',
    render: () => (
      <Space>
        <Button type="link" size="small">
          Edit
        </Button>
        <Button type="link" size="small" danger>
          Delete
        </Button>
      </Space>
    ),
  },
];

const tableData = [
  { key: '1', name: 'John Brown', age: 32, address: 'New York' },
  { key: '2', name: 'Jim Green', age: 42, address: 'London' },
  { key: '3', name: 'Joe Black', age: 32, address: 'Sydney' },
  { key: '4', name: 'Jane Doe', age: 28, address: 'Paris' },
];

const treeData = [
  {
    title: 'Parent 1',
    key: '0-0',
    children: [
      {
        title: 'Child 1-1',
        key: '0-0-0',
        children: [{ title: 'Leaf', key: '0-0-0-0' }],
      },
      { title: 'Child 1-2', key: '0-0-1' },
    ],
  },
  {
    title: 'Parent 2',
    key: '0-1',
    children: [{ title: 'Child 2-1', key: '0-1-0' }],
  },
];

const menuItems = [
  { key: 'home', icon: <HomeOutlined />, label: 'Home' },
  {
    key: 'sub1',
    icon: <SettingOutlined />,
    label: 'Settings',
    children: [
      { key: 'sub1-1', label: 'Option 1' },
      { key: 'sub1-2', label: 'Option 2' },
    ],
  },
  { key: 'users', icon: <UserOutlined />, label: 'Users' },
  { key: 'disabled', label: 'Disabled', disabled: true },
];

const stepsItems = [
  { title: 'Finished', description: 'This is a description.' },
  {
    title: 'In Progress',
    description: 'This is a description.',
    subTitle: '00:00:08',
  },
  { title: 'Waiting', description: 'This is a description.' },
];

const descItems = [
  { key: '1', label: 'Product', children: 'Cloud Database' },
  { key: '2', label: 'Billing', children: 'Prepaid' },
  { key: '3', label: 'Auto Renewal', children: 'YES' },
  {
    key: '4',
    label: 'Status',
    children: <Badge status="processing" text="Running" />,
  },
];

const timelineItems = [
  { children: 'Create a services site 2015-09-01' },
  { children: 'Solve initial network problems', color: 'green' },
  {
    dot: <SmileOutlined style={{ fontSize: 16 }} />,
    children: 'Technical testing',
    color: 'red',
  },
  { children: 'Network problems being solved 2015-09-01' },
];

const collapseItems = [
  {
    key: '1',
    label: 'Panel 1',
    children: (
      <Paragraph>
        Content of Panel 1. Ant Design is a design system for enterprise-level
        products.
      </Paragraph>
    ),
  },
  {
    key: '2',
    label: 'Panel 2',
    children: (
      <Paragraph>
        Content of Panel 2. A design language and React UI library.
      </Paragraph>
    ),
  },
  {
    key: '3',
    label: 'Disabled Panel',
    children: <Paragraph>Content of Panel 3.</Paragraph>,
    collapsible: 'disabled' as const,
  },
];

const dropdownMenuItems = {
  items: [
    { key: '1', label: 'Action 1' },
    { key: '2', label: 'Action 2' },
    { type: 'divider' as const },
    { key: '3', label: 'Action (Danger)', danger: true },
  ],
};

const transferSource = Array.from({ length: 8 }, (_, idx) => ({
  key: String(idx),
  title: `Item ${idx + 1}`,
  description: `Description of item ${idx + 1}`,
}));

// ─── Color Groups ────────────────────────────────────────────────────────────

const colorGroups: { group: string; tokenNames: string[] }[] = [
  {
    group: 'Primary',
    tokenNames: [
      'colorPrimary',
      'colorPrimaryHover',
      'colorPrimaryActive',
      'colorPrimaryBg',
      'colorPrimaryBgHover',
      'colorPrimaryBorder',
      'colorPrimaryBorderHover',
      'colorPrimaryText',
      'colorPrimaryTextHover',
      'colorPrimaryTextActive',
    ],
  },
  {
    group: 'Success',
    tokenNames: [
      'colorSuccess',
      'colorSuccessHover',
      'colorSuccessActive',
      'colorSuccessBg',
      'colorSuccessBgHover',
      'colorSuccessBorder',
      'colorSuccessBorderHover',
      'colorSuccessText',
    ],
  },
  {
    group: 'Warning',
    tokenNames: [
      'colorWarning',
      'colorWarningHover',
      'colorWarningActive',
      'colorWarningBg',
      'colorWarningBgHover',
      'colorWarningBorder',
      'colorWarningBorderHover',
      'colorWarningText',
    ],
  },
  {
    group: 'Error',
    tokenNames: [
      'colorError',
      'colorErrorHover',
      'colorErrorActive',
      'colorErrorBg',
      'colorErrorBgHover',
      'colorErrorBorder',
      'colorErrorBorderHover',
      'colorErrorText',
    ],
  },
  {
    group: 'Info',
    tokenNames: [
      'colorInfo',
      'colorInfoHover',
      'colorInfoActive',
      'colorInfoBg',
      'colorInfoBgHover',
      'colorInfoBorder',
      'colorInfoBorderHover',
      'colorInfoText',
    ],
  },
  {
    group: 'Text',
    tokenNames: [
      'colorText',
      'colorTextSecondary',
      'colorTextTertiary',
      'colorTextQuaternary',
      'colorTextDisabled',
      'colorTextHeading',
      'colorTextLightSolid',
    ],
  },
  {
    group: 'Background',
    tokenNames: [
      'colorBgBase',
      'colorBgLayout',
      'colorBgContainer',
      'colorBgElevated',
      'colorBgSpotlight',
      'colorBgMask',
    ],
  },
  {
    group: 'Border',
    tokenNames: ['colorBorder', 'colorBorderSecondary'],
  },
  {
    group: 'Fill',
    tokenNames: [
      'colorFill',
      'colorFillSecondary',
      'colorFillTertiary',
      'colorFillQuaternary',
      'colorFillContent',
      'colorFillContentHover',
      'colorFillAlter',
    ],
  },
  {
    group: 'Link',
    tokenNames: ['colorLink', 'colorLinkHover', 'colorLinkActive'],
  },
];

// ─── Layout Constants ─────────────────────────────────────────────────────────

const HEADER_HEIGHT = 64;
const SIDER_WIDTH = 220;

// ─── Nav Sections ─────────────────────────────────────────────────────────────

const navSections = [
  { key: 'colors', label: 'Color Palette' },
  { key: 'typography', label: 'Typography' },
  { key: 'buttons', label: 'Buttons' },
  { key: 'tags-badges', label: 'Tags & Badges' },
  { key: 'avatars', label: 'Avatars' },
  { key: 'form', label: 'Form & Data Entry' },
  { key: 'tabs', label: 'Tabs' },
  { key: 'collapse', label: 'Collapse' },
  { key: 'table', label: 'Table' },
  { key: 'list', label: 'List' },
  { key: 'tree', label: 'Tree' },
  { key: 'descriptions', label: 'Descriptions & Stats' },
  { key: 'ib-statistic', label: 'IBStatistic' },
  { key: 'timeline', label: 'Timeline' },
  { key: 'alerts', label: 'Alerts' },
  { key: 'progress', label: 'Progress' },
  { key: 'spin', label: 'Spin & Skeleton' },
  { key: 'result', label: 'Result' },
  { key: 'breadcrumb', label: 'Breadcrumb & Pagination' },
  { key: 'steps', label: 'Steps' },
  { key: 'menu', label: 'Menu' },
  { key: 'tooltip', label: 'Tooltip & Popover' },
  { key: 'overlays', label: 'Modals & Drawers' },
  { key: 'cards', label: 'Cards' },
  { key: 'transfer', label: 'Transfer' },
  { key: 'empty', label: 'Empty State' },
  { key: 'calendar', label: 'Calendar' },
  { key: 'carousel', label: 'Carousel' },
  { key: 'divider', label: 'Divider & Space' },
  { key: 'severity', label: 'Severity' },
];

// ─── Color Swatch ────────────────────────────────────────────────────────────

const ColorSwatch = ({
  name,
  value,
  copyText,
}: {
  name: string;
  value: string;
  copyText?: string;
}) => {
  const { token } = antTheme.useToken();
  return (
    <Tooltip title={value} placement="bottom">
      <Flex vertical gap={4} style={{ width: 136 }}>
        <div
          style={{
            height: 52,
            borderRadius: token.borderRadiusSM,
            border: `1px solid ${token.colorBorderSecondary}`,
            background: `
              linear-gradient(${value}, ${value}),
              repeating-conic-gradient(#bbb 0% 25%, #eee 0% 50%) 0 0 / 10px 10px
            `,
          }}
        />
        <Text
          copyable={{ text: copyText ?? `token.${name}` }}
          style={{ fontSize: 11, lineHeight: 1.4 }}
        >
          {name}
        </Text>
      </Flex>
    </Tooltip>
  );
};

// ─── Section Wrapper ──────────────────────────────────────────────────────────

const Section = ({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: React.ReactNode;
}) => (
  <div id={id} style={{ scrollMarginTop: HEADER_HEIGHT + 8 }}>
    <Card title={title} style={{ marginBottom: 24 }}>
      {children}
    </Card>
  </div>
);

// ─── Page Content (reads live theme tokens) ───────────────────────────────────

const PageContent = ({
  isDark,
  onToggle,
}: {
  isDark: boolean;
  onToggle: (val: boolean) => void;
}) => {
  const { token } = antTheme.useToken();
  const [messageApi, msgContextHolder] = message.useMessage();
  const [notifApi, notifContextHolder] = notification.useNotification();
  const [modalOpen, setModalOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [transferTargets, setTransferTargets] = useState<string[]>([]);

  const componentColorGroups = Object.entries(
    (isDark ? darkThemeData : lightThemeData).components as Record<
      string,
      Record<string, unknown>
    >,
  )
    .map(([group, tokens]) => ({
      group,
      entries: Object.entries(tokens)
        .filter(
          ([key, val]) =>
            typeof val === 'string' &&
            (key.startsWith('color') ||
              (val as string).startsWith('#') ||
              (val as string).startsWith('rgb') ||
              (val as string).startsWith('hsl')),
        )
        .map(([key, val]) => ({
          name: `${group}.${key}`,
          value: val as string,
        })),
    }))
    .filter(({ entries }) => entries.length > 0);

  // @ts-ignore
  return (
    <Layout>
      {msgContextHolder}
      {notifContextHolder}

      {/* ── Fixed Header ── */}
      <Layout.Header
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 200,
          height: HEADER_HEIGHT,
          background: token.colorBgContainer,
          borderBottom: `1px solid ${token.colorBorderSecondary}`,
          padding: '0 24px',
        }}
      >
        <Flex justify="space-between" align="center" style={{ height: '100%' }}>
          <Title level={4} style={{ margin: 0 }}>
            Ant Design Theme Preview
          </Title>
          <Flex align="center" gap={8}>
            <Text>Light</Text>
            <Switch checked={isDark} onChange={onToggle} />
            <Text>Dark</Text>
          </Flex>
        </Flex>
      </Layout.Header>

      {/* ── Fixed Sider ── */}
      <Layout.Sider
        width={SIDER_WIDTH}
        style={{
          position: 'fixed',
          top: HEADER_HEIGHT,
          bottom: 0,
          left: 0,
          overflow: 'auto',
          background: token.colorBgContainer,
          borderRight: `1px solid ${token.colorBorderSecondary}`,
        }}
      >
        <Anchor
          affix={false}
          targetOffset={HEADER_HEIGHT + 8}
          style={{ padding: '16px 8px' }}
          onClick={(ev, link) => {
            ev.preventDefault();
            const target = document.getElementById(
              link.href.replace(/.*#/, ''),
            );
            if (target) target.scrollIntoView({ behavior: 'smooth' });
          }}
          items={navSections.map(({ key, label }) => ({
            key,
            href: `#${key}`,
            title: label,
          }))}
        />
      </Layout.Sider>

      {/* ── Scrollable Content ── */}
      <Layout
        style={{
          marginTop: HEADER_HEIGHT,
          marginLeft: SIDER_WIDTH,
          background: token.colorBgLayout,
        }}
      >
        <Layout.Content style={{ padding: 24 }}>
          {/* ── Color Palette ── */}
          <Section id="colors" title="Color Palette">
            {colorGroups.map(({ group, tokenNames }, idx) => (
              <div key={group}>
                {idx > 0 && <Divider style={{ marginTop: 8 }} />}
                <Title level={5} style={{ marginBottom: 12 }}>
                  {group}
                </Title>
                <Flex wrap gap={12}>
                  {tokenNames.map((name) => (
                    <ColorSwatch
                      key={name}
                      name={name}
                      value={(token as unknown as Record<string, string>)[name]}
                    />
                  ))}
                </Flex>
              </div>
            ))}

            <Divider style={{ marginTop: 8 }} />
            <Title level={5} style={{ marginBottom: 4 }}>
              Component Overrides
            </Title>
            <Text
              type="secondary"
              style={{ display: 'block', marginBottom: 16, fontSize: 12 }}
            >
              Colors set via the <Text code>components</Text> section of the
              theme config — not part of the global token set.
            </Text>
            {componentColorGroups.map(({ group, entries }) => (
              <div key={group} style={{ marginBottom: 20 }}>
                <Text
                  strong
                  style={{ display: 'block', marginBottom: 8, fontSize: 12 }}
                >
                  {group}
                </Text>
                <Flex wrap gap={12}>
                  {entries.map(({ name, value }) => (
                    <ColorSwatch
                      key={name}
                      name={name}
                      value={value}
                      copyText={`components.${name}`}
                    />
                  ))}
                </Flex>
              </div>
            ))}
          </Section>

          {/* ── Typography ── */}
          <Section id="typography" title="Typography">
            <Space orientation="vertical" style={{ width: '100%' }}>
              <Title level={1}>h1. Ant Design</Title>
              <Title level={2}>h2. Ant Design</Title>
              <Title level={3}>h3. Ant Design</Title>
              <Title level={4}>h4. Ant Design</Title>
              <Title level={5}>h5. Ant Design</Title>
              <Divider titlePlacement="left">Text</Divider>
              {(
                [
                  ['Default', <Text key={0}>The quick brown fox</Text>],
                  [
                    'Secondary',
                    <Text key={0} type="secondary">
                      The quick brown fox
                    </Text>,
                  ],
                  [
                    'Success',
                    <Text key={0} type="success">
                      The quick brown fox
                    </Text>,
                  ],
                  [
                    'Warning',
                    <Text key={0} type="warning">
                      The quick brown fox
                    </Text>,
                  ],
                  [
                    'Danger',
                    <Text key={0} type="danger">
                      The quick brown fox
                    </Text>,
                  ],
                  [
                    'Disabled',
                    <Text key={0} disabled>
                      The quick brown fox
                    </Text>,
                  ],
                  [
                    'Mark',
                    <Text key={0} mark>
                      The quick brown fox
                    </Text>,
                  ],
                  [
                    'Code',
                    <Text key={0} code>
                      The quick brown fox
                    </Text>,
                  ],
                  [
                    'Keyboard',
                    <Text key={0} keyboard>
                      Ctrl
                    </Text>,
                  ],
                  [
                    'Underline',
                    <Text key={0} underline>
                      The quick brown fox
                    </Text>,
                  ],
                  [
                    'Strikethrough',
                    <Text key={0} delete>
                      The quick brown fox
                    </Text>,
                  ],
                  [
                    'Strong',
                    <Text key={0} strong>
                      The quick brown fox
                    </Text>,
                  ],
                  [
                    'Italic',
                    <Text key={0} italic>
                      The quick brown fox
                    </Text>,
                  ],
                  [
                    'Link',
                    <Link key={0} href="#">
                      The quick brown fox
                    </Link>,
                  ],
                ] as [string, React.ReactNode][]
              ).map(([label, example]) => (
                <Flex key={label} align="baseline" gap={16}>
                  <Text
                    type="secondary"
                    style={{ width: 110, flexShrink: 0, fontSize: 12 }}
                  >
                    {label}
                  </Text>
                  {example}
                </Flex>
              ))}
              <Divider titlePlacement="left">Paragraph</Divider>
              <Paragraph>
                Ant Design is a design system for enterprise-level products.
                Create an efficient and enjoyable work experience. Following the
                Ant Design specification, we developed a React UI library{' '}
                <Text code>antd</Text> that contains a set of high quality
                components and demos for building rich, interactive user
                interfaces.
              </Paragraph>
            </Space>
          </Section>

          {/* ── Buttons ── */}
          <Section id="buttons" title="Buttons">
            <Space orientation="vertical" style={{ width: '100%' }}>
              <Space wrap>
                <Button type="primary">Primary</Button>
                <Button>Default</Button>
                <Button type="dashed">Dashed</Button>
                <Button type="text">Text</Button>
                <Button type="link">Link</Button>
              </Space>
              <Space wrap>
                <Button type="primary" danger>
                  Primary Danger
                </Button>
                <Button danger>Default Danger</Button>
                <Button type="dashed" danger>
                  Dashed Danger
                </Button>
                <Button type="text" danger>
                  Text Danger
                </Button>
                <Button type="link" danger>
                  Link Danger
                </Button>
              </Space>
              <Space wrap>
                <Button type="primary" disabled>
                  Disabled
                </Button>
                <Button type="primary" loading>
                  Loading
                </Button>
                <Button type="primary" icon={<SearchOutlined />}>
                  With Icon
                </Button>
                <Button type="primary" icon={<PlusOutlined />} shape="circle" />
                <Button type="primary" shape="round">
                  Round
                </Button>
              </Space>
              <Space wrap align="center">
                <Button type="primary" size="large">
                  Large
                </Button>
                <Button type="primary" size="middle">
                  Middle
                </Button>
                <Button type="primary" size="small">
                  Small
                </Button>
              </Space>
            </Space>
          </Section>

          {/* ── Tags & Badges ── */}
          <Section id="tags-badges" title="Tags & Badges">
            <Space orientation="vertical" style={{ width: '100%' }}>
              <Space wrap>
                <Tag>Default</Tag>
                <Tag color="success">Success</Tag>
                <Tag color="processing">Processing</Tag>
                <Tag color="error">Error</Tag>
                <Tag color="warning">Warning</Tag>
                <Tag color="magenta">Magenta</Tag>
                <Tag color="red">Red</Tag>
                <Tag color="volcano">Volcano</Tag>
                <Tag color="orange">Orange</Tag>
                <Tag color="gold">Gold</Tag>
                <Tag color="lime">Lime</Tag>
                <Tag color="green">Green</Tag>
                <Tag color="cyan">Cyan</Tag>
                <Tag color="blue">Blue</Tag>
                <Tag color="geekblue">Geekblue</Tag>
                <Tag color="purple">Purple</Tag>
              </Space>
              <Space wrap>
                <Tag icon={<SyncOutlined spin />} color="processing">
                  Processing
                </Tag>
                <Tag icon={<CheckCircleOutlined />} color="success">
                  Success
                </Tag>
                <Tag icon={<ExclamationCircleOutlined />} color="warning">
                  Warning
                </Tag>
              </Space>
              <Divider />
              <Space wrap>
                <Badge count={5}>
                  <Avatar shape="square" size="large" />
                </Badge>
                <Badge count={0} showZero>
                  <Avatar shape="square" size="large" />
                </Badge>
                <Badge
                  count={<BellOutlined style={{ color: token.colorError }} />}
                >
                  <Avatar shape="square" size="large" />
                </Badge>
                <Badge dot>
                  <Avatar shape="square" size="large" />
                </Badge>
                <Badge status="success" text="Success" />
                <Badge status="error" text="Error" />
                <Badge status="default" text="Default" />
                <Badge status="processing" text="Processing" />
                <Badge status="warning" text="Warning" />
              </Space>
            </Space>
          </Section>

          {/* ── Avatars ── */}
          <Section id="avatars" title="Avatars">
            <Space wrap align="center">
              <Avatar size={64} icon={<UserOutlined />} />
              <Avatar size="large" icon={<UserOutlined />} />
              <Avatar icon={<UserOutlined />} />
              <Avatar size="small" icon={<UserOutlined />} />
              <Avatar shape="square" size="large" icon={<UserOutlined />} />
              <Avatar style={{ background: token.colorPrimary }}>AB</Avatar>
              <Avatar style={{ background: token.colorSuccess }}>OK</Avatar>
              <Avatar style={{ background: token.colorError }}>ER</Avatar>
              <Avatar style={{ background: token.colorWarning }}>WN</Avatar>
            </Space>
          </Section>

          {/* ── Form & Data Entry ── */}
          <Section id="form" title="Form & Data Entry">
            <Form layout="vertical" style={{ maxWidth: 600 }}>
              <Form.Item label="Input">
                <Input placeholder="Basic input" />
              </Form.Item>
              <Form.Item label="Input with Affix">
                <Input
                  prefix={<UserOutlined />}
                  placeholder="Username"
                  suffix={
                    <Tooltip title="Extra information">
                      <InfoCircleOutlined />
                    </Tooltip>
                  }
                />
              </Form.Item>
              <Form.Item label="Password">
                <Input.Password placeholder="Password" />
              </Form.Item>
              <Form.Item label="Search">
                <Input.Search placeholder="Search..." enterButton />
              </Form.Item>
              <Form.Item label="TextArea">
                <Input.TextArea rows={3} placeholder="TextArea..." />
              </Form.Item>
              <Form.Item label="Number">
                <InputNumber
                  min={1}
                  max={100}
                  defaultValue={42}
                  style={{ width: '100%' }}
                />
              </Form.Item>
              <Form.Item label="Select">
                <Select defaultValue="option1" style={{ width: '100%' }}>
                  <Select.Option value="option1">Option 1</Select.Option>
                  <Select.Option value="option2">Option 2</Select.Option>
                  <Select.Option value="option3" disabled>
                    Option 3 (Disabled)
                  </Select.Option>
                </Select>
              </Form.Item>
              <Form.Item label="Multi-Select">
                <Select
                  mode="multiple"
                  defaultValue={['option1', 'option2']}
                  style={{ width: '100%' }}
                  options={[
                    { value: 'option1', label: 'Option 1' },
                    { value: 'option2', label: 'Option 2' },
                    { value: 'option3', label: 'Option 3' },
                  ]}
                />
              </Form.Item>
              <Form.Item label="Date Picker">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item label="Date Range">
                <DatePicker.RangePicker style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item label="Time Picker">
                <TimePicker style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item label="Checkbox">
                <Space orientation="vertical">
                  <Checkbox>Standalone Checkbox</Checkbox>
                  <Checkbox.Group
                    options={['Option A', 'Option B', 'Option C']}
                    defaultValue={['Option A']}
                  />
                </Space>
              </Form.Item>
              <Form.Item label="Radio">
                <Radio.Group defaultValue="a">
                  <Radio value="a">Option A</Radio>
                  <Radio value="b">Option B</Radio>
                  <Radio value="c">Option C</Radio>
                  <Radio value="d" disabled>
                    Disabled
                  </Radio>
                </Radio.Group>
              </Form.Item>
              <Form.Item label="Radio Button">
                <Radio.Group defaultValue="a" buttonStyle="solid">
                  <Radio.Button value="a">A</Radio.Button>
                  <Radio.Button value="b">B</Radio.Button>
                  <Radio.Button value="c">C</Radio.Button>
                  <Radio.Button value="d" disabled>
                    D
                  </Radio.Button>
                </Radio.Group>
              </Form.Item>
              <Form.Item label="Switch">
                <Space>
                  <Switch defaultChecked />
                  <Switch />
                  <Switch loading defaultChecked />
                  <Switch size="small" defaultChecked />
                </Space>
              </Form.Item>
              <Form.Item label="Slider">
                <Slider defaultValue={37} />
              </Form.Item>
              <Form.Item label="Slider Range">
                <Slider range defaultValue={[20, 50]} />
              </Form.Item>
              <Form.Item label="Rate">
                <Space orientation="vertical">
                  <Rate defaultValue={3} />
                  <Rate allowHalf defaultValue={2.5} />
                  <Rate character={<HeartOutlined />} defaultValue={3} />
                </Space>
              </Form.Item>
              <Form.Item label="Color Picker">
                <ColorPicker defaultValue={token.colorPrimary} showText />
              </Form.Item>
              <Form.Item label="Upload">
                <Upload.Dragger name="file" multiple>
                  <p>
                    <CloudUploadOutlined style={{ fontSize: 32 }} />
                  </p>
                  <p>Click or drag file to this area to upload</p>
                  <p>
                    <Text type="secondary">
                      Support for a single or bulk upload.
                    </Text>
                  </p>
                </Upload.Dragger>
              </Form.Item>
            </Form>
          </Section>

          {/* ── Tabs ── */}
          <Section id="tabs" title="Tabs">
            <Space orientation="vertical" style={{ width: '100%' }}>
              <Tabs
                defaultActiveKey="1"
                items={[
                  {
                    key: '1',
                    label: 'Tab 1',
                    children: <Paragraph>Content of Tab 1</Paragraph>,
                  },
                  {
                    key: '2',
                    label: 'Tab 2',
                    children: <Paragraph>Content of Tab 2</Paragraph>,
                  },
                  {
                    key: '3',
                    label: 'Tab 3',
                    children: <Paragraph>Content of Tab 3</Paragraph>,
                  },
                  {
                    key: '4',
                    label: 'Disabled',
                    disabled: true,
                    children: null,
                  },
                ]}
              />
              <Tabs
                defaultActiveKey="1"
                type="card"
                items={[
                  {
                    key: '1',
                    label: 'Card Tab 1',
                    children: <Paragraph>Content of Card Tab 1</Paragraph>,
                  },
                  {
                    key: '2',
                    label: 'Card Tab 2',
                    children: <Paragraph>Content of Card Tab 2</Paragraph>,
                  },
                ]}
              />
            </Space>
          </Section>

          {/* ── Collapse ── */}
          <Section id="collapse" title="Collapse">
            <Space orientation="vertical" style={{ width: '100%' }}>
              <Collapse defaultActiveKey={['1']} items={collapseItems} />
              <Collapse accordion items={collapseItems} />
            </Space>
          </Section>

          {/* ── Table ── */}
          <Section id="table" title="Table">
            <Table
              columns={tableColumns}
              dataSource={tableData}
              pagination={{ pageSize: 5 }}
              size="middle"
            />
          </Section>

          {/* ── List ── */}
          <Section id="list" title="List">
            <List
              itemLayout="horizontal"
              dataSource={['Item One', 'Item Two', 'Item Three', 'Item Four']}
              renderItem={(item) => (
                <List.Item
                  actions={[
                    <Button type="link" key="edit">
                      Edit
                    </Button>,
                    <Button type="link" key="more">
                      More
                    </Button>,
                  ]}
                >
                  <List.Item.Meta
                    avatar={<Avatar icon={<UserOutlined />} />}
                    title={<Link href="#">{item}</Link>}
                    description="Ant Design is a design language for background applications."
                  />
                </List.Item>
              )}
            />
          </Section>

          {/* ── Tree ── */}
          <Section id="tree" title="Tree">
            <Space>
              <Tree defaultExpandAll treeData={treeData} />
              <Tree checkable defaultExpandAll treeData={treeData} />
            </Space>
          </Section>

          {/* ── Descriptions & Statistics ── */}
          <Section id="descriptions" title="Descriptions & Statistics">
            <Descriptions title="Product Info" bordered items={descItems} />
            <Divider />
            <Space size="large" wrap>
              <Statistic title="Active Users" value={112893} />
              <Statistic
                title="Account Balance"
                value={9200}
                precision={2}
                prefix="$"
              />
              <Statistic
                title="Growth Rate"
                value={11.28}
                precision={2}
                suffix="%"
                valueStyle={{ color: token.colorSuccess }}
                prefix={<LikeOutlined />}
              />
              <Statistic
                title="Error Rate"
                value={9.3}
                precision={2}
                suffix="%"
                valueStyle={{ color: token.colorError }}
              />
              <Statistic.Countdown
                title="Deadline"
                value={Date.now() + 1000 * 60 * 60 * 24}
                format="HH:mm:ss"
              />
            </Space>
          </Section>

          {/* ── IBStatistic ── */}
          <Section id="ib-statistic" title="IBStatistic">
            <Row gutter={16}>
              <Col span={4}>
                <IBStatistic
                  title="Info"
                  subTitle={'Informational'}
                  icon={<InfoCircleOutlined />}
                  value={42}
                  style={statisticsStyles.info}
                  valueStyle={{ color: statisticsStyles.info.color }}
                />
              </Col>
              <Col span={4}>
                <IBStatistic
                  title="OK"
                  subTitle={'All Good'}
                  icon={<CheckCircleOutlined />}
                  value={99}
                  style={statisticsStyles.ok}
                  valueStyle={{ color: statisticsStyles.ok.color }}
                />
              </Col>
              <Col span={4}>
                <IBStatistic
                  title="Warning"
                  subTitle={'May need attention'}
                  icon={<WarningOutlined />}
                  value={7}
                  style={statisticsStyles.warning}
                  valueStyle={{ color: statisticsStyles.warning.color }}
                />
              </Col>
              <Col span={4}>
                <IBStatistic
                  title="Critical"
                  subTitle={'Needs Attention'}
                  icon={<ExclamationCircleOutlined />}
                  value={3}
                  style={statisticsStyles.critical}
                  valueStyle={{ color: statisticsStyles.critical.color }}
                />
              </Col>
              <Col span={4}>
                <IBStatistic
                  title="Neutral"
                  subTitle={'Neutral Information / Default'}
                  value={128}
                  style={statisticsStyles.neutral}
                  valueStyle={{ color: statisticsStyles.neutral.color }}
                />
              </Col>
            </Row>
          </Section>

          {/* ── Timeline ── */}
          <Section id="timeline" title="Timeline">
            <Timeline items={timelineItems} />
          </Section>

          {/* ── Alerts ── */}
          <Section id="alerts" title="Alerts">
            <Space orientation="vertical" style={{ width: '100%' }}>
              <Alert message="Success Alert" type="success" showIcon />
              <Alert message="Info Alert" type="info" showIcon />
              <Alert message="Warning Alert" type="warning" showIcon />
              <Alert message="Error Alert" type="error" showIcon />
              <Alert
                message="Success with description"
                description="More detailed description of the success state."
                type="success"
                showIcon
                closable
              />
              <Alert
                message="Error with description"
                description="More detailed description of the error state."
                type="error"
                showIcon
                closable
              />
            </Space>
          </Section>

          {/* ── Progress ── */}
          <Section id="progress" title="Progress">
            <Space orientation="vertical" style={{ width: '100%' }}>
              <Progress percent={30} />
              <Progress percent={50} status="active" />
              <Progress percent={70} status="exception" />
              <Progress percent={100} />
              <Space wrap>
                <Progress type="circle" percent={75} />
                <Progress type="circle" percent={70} status="exception" />
                <Progress type="circle" percent={100} />
                <Progress type="dashboard" percent={75} />
              </Space>
              <Progress steps={8} percent={50} />
            </Space>
          </Section>

          {/* ── Spin & Skeleton ── */}
          <Section id="spin" title="Spin & Skeleton">
            <Space size="large" wrap>
              <Spin size="small" />
              <Spin />
              <Spin size="large" />
              <Spin
                indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
              />
            </Space>
            <Divider />
            <Skeleton />
            <Skeleton avatar paragraph={{ rows: 3 }} />
            <Space>
              <Skeleton.Button active />
              <Skeleton.Input active style={{ width: 200 }} />
              <Skeleton.Avatar active />
            </Space>
          </Section>

          {/* ── Result ── */}
          <Section id="result" title="Result">
            <Flex wrap gap={24}>
              <Result
                status="success"
                title="Successfully!"
                subTitle="Operation completed successfully."
                extra={<Button type="primary">Go Home</Button>}
                style={{ flex: 1 }}
              />
              <Result
                status="error"
                title="Submission Failed"
                subTitle="Please check and modify the following information before resubmitting."
                extra={<Button type="primary">Try Again</Button>}
                style={{ flex: 1 }}
              />
              <Result
                status="warning"
                title="Warning"
                subTitle="There are some problems with your operation."
                extra={<Button type="primary">Go Console</Button>}
                style={{ flex: 1 }}
              />
              <Result
                status="info"
                title="Your operation has been executed"
                extra={<Button type="primary">Go Console</Button>}
                style={{ flex: 1 }}
              />
            </Flex>
          </Section>

          {/* ── Breadcrumb & Pagination ── */}
          <Section id="breadcrumb" title="Breadcrumb & Pagination">
            <Space orientation="vertical" style={{ width: '100%' }}>
              <Breadcrumb
                items={[
                  { title: <HomeOutlined /> },
                  { title: 'Application Center' },
                  { title: 'Application List' },
                  { title: 'An Application' },
                ]}
              />
              <Divider />
              <Pagination
                defaultCurrent={1}
                total={50}
                showSizeChanger
                showQuickJumper
              />
              <Pagination
                defaultCurrent={6}
                total={500}
                showSizeChanger
                showQuickJumper
                showTotal={(total) => `Total ${total} items`}
              />
            </Space>
          </Section>

          {/* ── Steps ── */}
          <Section id="steps" title="Steps">
            <Space orientation="vertical" style={{ width: '100%' }}>
              <Steps current={1} items={stepsItems} />
              <Steps current={1} status="error" items={stepsItems} />
              <Steps current={1} direction="vertical" items={stepsItems} />
              <Steps current={1} size="small" items={stepsItems} />
            </Space>
          </Section>

          {/* ── Menu ── */}
          <Section id="menu" title="Menu">
            <Space orientation="vertical" style={{ width: '100%' }}>
              <Menu
                mode="horizontal"
                defaultSelectedKeys={['home']}
                items={menuItems}
              />
              <Divider />
              <Menu
                mode="inline"
                defaultSelectedKeys={['home']}
                defaultOpenKeys={['sub1']}
                style={{ width: 256 }}
                items={menuItems}
              />
            </Space>
          </Section>

          {/* ── Tooltip, Popover & Popconfirm ── */}
          <Section id="tooltip" title="Tooltip, Popover & Popconfirm">
            <Space wrap>
              <Tooltip title="This is a tooltip">
                <Button>Hover for Tooltip</Button>
              </Tooltip>
              <Popover
                title="Popover Title"
                content={
                  <Paragraph>
                    This is the popover content. It can contain{' '}
                    <Link href="#">links</Link> and more.
                  </Paragraph>
                }
              >
                <Button>Click for Popover</Button>
              </Popover>
              <Popconfirm
                title="Delete the task"
                description="Are you sure you want to delete this task?"
                okText="Yes"
                cancelText="No"
              >
                <Button danger>Delete (Popconfirm)</Button>
              </Popconfirm>
            </Space>
          </Section>

          {/* ── Dropdown, Modal, Drawer, Message & Notification ── */}
          <Section
            id="overlays"
            title="Dropdown, Modal, Drawer, Message & Notification"
          >
            <Space wrap>
              <Dropdown menu={dropdownMenuItems}>
                <Button>
                  Dropdown <DownOutlined />
                </Button>
              </Dropdown>
              <Button type="primary" onClick={() => setModalOpen(true)}>
                Open Modal
              </Button>
              <Button onClick={() => setDrawerOpen(true)}>Open Drawer</Button>
              <Button
                onClick={() => messageApi.success('This is a success message!')}
              >
                Show Message
              </Button>
              <Button
                onClick={() =>
                  notifApi.open({
                    message: 'Notification Title',
                    description: 'This is a notification message.',
                  })
                }
              >
                Show Notification
              </Button>
            </Space>

            <Modal
              title="Modal Title"
              open={modalOpen}
              onOk={() => setModalOpen(false)}
              onCancel={() => setModalOpen(false)}
            >
              <Paragraph>
                Some modal content here. You can put any components inside.
              </Paragraph>
              <Input placeholder="Input inside modal" />
            </Modal>

            <Drawer
              title="Drawer Title"
              open={drawerOpen}
              onClose={() => setDrawerOpen(false)}
            >
              <Paragraph>Some drawer content here.</Paragraph>
              <Button type="primary" block>
                Action in Drawer
              </Button>
            </Drawer>
          </Section>

          {/* ── Cards ── */}
          <Section id="cards" title="Cards">
            <Flex gap={16} wrap>
              <Card
                title="Default Card"
                extra={<Link href="#">More</Link>}
                style={{ flex: 1 }}
              >
                <Paragraph>Card content goes here.</Paragraph>
              </Card>
              <Card
                title="Card with Actions"
                extra={<Link href="#">More</Link>}
                actions={[
                  <SettingOutlined key="setting" />,
                  <StarOutlined key="star" />,
                  <LikeOutlined key="like" />,
                ]}
                style={{ flex: 1 }}
              >
                <Paragraph>Card with action buttons in the footer.</Paragraph>
              </Card>
              <Card
                hoverable
                cover={
                  <div style={{ background: token.colorPrimary, height: 4 }} />
                }
                style={{ flex: 1 }}
              >
                <Card.Meta
                  avatar={<Avatar icon={<UserOutlined />} />}
                  title="Card with Cover"
                  description="A description for this card"
                />
              </Card>
            </Flex>
          </Section>

          {/* ── Transfer ── */}
          <Section id="transfer" title="Transfer">
            <Transfer
              dataSource={transferSource}
              titles={['Source', 'Target']}
              targetKeys={transferTargets}
              onChange={(newTargetKeys) =>
                setTransferTargets(newTargetKeys as string[])
              }
              render={(item) => item.title}
            />
          </Section>

          {/* ── Empty ── */}
          <Section id="empty" title="Empty State">
            <Flex gap={24} wrap justify="space-around">
              <Empty />
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
              <Empty
                description={<Text type="secondary">No data available</Text>}
              >
                <Button type="primary">Create Now</Button>
              </Empty>
            </Flex>
          </Section>

          {/* ── Calendar ── */}
          <Section id="calendar" title="Calendar">
            <Calendar fullscreen={false} />
          </Section>

          {/* ── Carousel ── */}
          <Section id="carousel" title="Carousel">
            <Carousel autoplay>
              {['#364d79', '#8c7ae6', '#1a6b3c', '#8e3c3c'].map((color) => (
                <div key={color}>
                  <Flex
                    align="center"
                    justify="center"
                    style={{ background: color, height: 120 }}
                  >
                    <Text style={{ color: '#fff', fontSize: 24 }}>Slide</Text>
                  </Flex>
                </div>
              ))}
            </Carousel>
          </Section>

          <Section id="divider" title="Divider & Space">
            <Divider>Default Divider with Text</Divider>
            <Divider titlePlacement="left">Left Placement</Divider>
            <Divider titlePlacement="right">Right Placement</Divider>
            <Divider dashed>Dashed</Divider>
            <Paragraph>
              <Text>Text</Text>
              <Divider orientation="vertical" />
              <Link href="#">Link 1</Link>
              <Divider orientation="vertical" />
              <Link href="#">Link 2</Link>
            </Paragraph>
            <Divider />
            <Space orientation="vertical">
              <Space size="small">
                <Button>Small</Button>
                <Button>Space</Button>
                <Button>Example</Button>
              </Space>
              <Space size="middle">
                <Button>Middle</Button>
                <Button>Space</Button>
                <Button>Example</Button>
              </Space>
              <Space size="large">
                <Button>Large</Button>
                <Button>Space</Button>
                <Button>Example</Button>
              </Space>
            </Space>
            <Divider />
            <Space.Compact>
              <Input placeholder="Compact" style={{ width: 160 }} />
              <Select defaultValue="1" style={{ width: 100 }}>
                <Select.Option value="1">Opt 1</Select.Option>
                <Select.Option value="2">Opt 2</Select.Option>
              </Select>
              <Button type="primary">Go</Button>
            </Space.Compact>
          </Section>

          {/* ── Severity ── */}
          <Section id="severity" title="Severity">
            <Space wrap>
              {[5, 4, 3, 2, 1, 0, -1].map((level) => (
                <div key={level} style={{ width: 100 }}>
                  <EventSeverityIndicator severity={level} />
                </div>
              ))}
            </Space>
          </Section>
        </Layout.Content>
      </Layout>
    </Layout>
  );
};

// ─── Theme Page ───────────────────────────────────────────────────────────────

const ThemePage = () => {
  const [isDark, setIsDark] = useState(false);

  return (
    <ConfigProvider theme={isDark ? darkThemeData : lightThemeData}>
      <PageContent isDark={isDark} onToggle={setIsDark} />
    </ConfigProvider>
  );
};

export default ThemePage;
